import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DP } from "./constants";

// demo-ui.html'deki .card + .card-head
export function Panel({ title, sub, action, children, sx }) {
  return (
    <Box
      sx={{
        bgcolor: DP.surface,
        border: `1px solid ${DP.border}`,
        borderRadius: DP.radius,
        boxShadow: DP.shadowSm,
        p: { xs: 2, md: 2.75 },
        mb: 3,
        overflow: "hidden",
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2.25,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 720,
                color: DP.textMain,
                fontSize: "1.05rem",
                lineHeight: 1.3,
                letterSpacing: "-0.018em",
              }}
            >
              {title}
            </Typography>
            {sub && (
              <Typography
                sx={{
                  color: DP.textMuted,
                  fontSize: "0.81rem",
                  lineHeight: 1.45,
                  letterSpacing: "0.002em",
                  mt: 0.3,
                }}
              >
                {sub}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

export function PrimaryButton(props) {
  return (
    <Button
      variant="contained"
      disableElevation
      {...props}
      sx={{
        bgcolor: DP.primary,
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: "-0.005em",
        borderRadius: "10px",
        minHeight: 40,
        px: 2,
        boxShadow: "0 8px 18px rgba(11,116,222,.2)",
        "&:hover": { bgcolor: DP.primaryDark },
        ...props.sx,
      }}
    />
  );
}

export function GhostButton(props) {
  return (
    <Button
      variant="outlined"
      {...props}
      sx={{
        color: DP.textMuted,
        borderColor: DP.border,
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: "-0.005em",
        borderRadius: "10px",
        minHeight: 40,
        px: 2,
        "&:hover": { borderColor: DP.primary, color: DP.primary, bgcolor: DP.primarySoft },
        ...props.sx,
      }}
    />
  );
}

// Form alanları: demo'daki .form-grid (otomatik sığan kolonlar)
export function FormGrid({ children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
        },
        gap: 2.25,
      }}
    >
      {children}
    </Box>
  );
}

// Filtreler geniş ekranda tek satırda; dar ekranlarda okunabilir biçimde kırılır.
export function FilterGrid({ children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(4, minmax(0, 1fr))",
        },
        gap: 2,
        alignItems: "start",
      }}
    >
      {children}
    </Box>
  );
}

// Listeler: demo'nun tablo görünümüne yakın, sade DataGrid.
export function ListGrid({
  rows,
  columns,
  getRowId,
  loading,
  onRowClick,
  height = 460,
  columnHeaderHeight = 56,
  initialSortModel = [],
}) {
  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      loading={loading}
      onRowClick={onRowClick}
      columnHeaderHeight={columnHeaderHeight}
      pagination
      initialState={{
        pagination: { paginationModel: { pageSize: 25 } },
        sorting: { sortModel: initialSortModel },
      }}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick
      sx={{
        height,
        border: `1px solid ${DP.border}`,
        borderRadius: "12px",
        bgcolor: DP.surface,
        overflow: "hidden",
        "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8fafc", borderColor: DP.border },
        "& .MuiDataGrid-columnHeaderTitleContainer": {
          justifyContent: "flex-start",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 720,
          color: DP.textMuted,
          fontSize: "0.74rem",
          textTransform: "uppercase",
          letterSpacing: "0.055em",
        },
        "& .MuiDataGrid-cell": {
          color: DP.textMain,
          fontSize: "0.875rem",
          lineHeight: 1.45,
          letterSpacing: "0.002em",
          borderColor: "#eef1f6",
          justifyContent: "flex-start",
          textAlign: "left",
        },
        "& .MuiDataGrid-actionsCell": { justifyContent: "flex-start" },
        "& .dp-balance-cell": {
          fontWeight: 750,
        },
        "& .MuiDataGrid-row": { transition: "background-color .15s ease" },
        "& .MuiDataGrid-row:hover": {
          bgcolor: DP.primarySoft,
          cursor: onRowClick ? "pointer" : "default",
        },
        "& .MuiDataGrid-footerContainer": {
          borderColor: DP.border,
          bgcolor: "#fbfcfe",
        },
        "& .MuiDataGrid-overlayWrapper": { minHeight: 180 },
      }}
    />
  );
}

// Sayı rozeti (Kart İzleme / dashboard durum etiketleri)
export function Badge({ tone = "primary", children }) {
  const color = DP[tone] || DP.primary;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        borderRadius: 999,
        bgcolor: `${color}1a`,
        color,
        fontSize: "0.75rem",
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: "0.005em",
      }}
    >
      {children}
    </Box>
  );
}
